import { useMemo } from 'react';
import { cn } from '../../lib/utils';
import { Label } from '../Label';
import { Separator } from '../Separator';
import { fieldVariants } from './variants';
import type {
  FieldSetProps,
  FieldLegendProps,
  FieldGroupProps,
  FieldProps,
  FieldContentProps,
  FieldLabelProps,
  FieldTitleProps,
  FieldDescriptionProps,
  FieldSeparatorProps,
  FieldErrorProps,
} from './types';

function FieldSet({ className, ...props }: FieldSetProps) {
  return (
    <fieldset
      data-slot='field-set'
      className={cn(
        'eye:flex eye:flex-col eye:gap-24',
        'eye:has-[>[data-slot=checkbox-group]]:gap-12 eye:has-[>[data-slot=radio-group]]:gap-12',
        className,
      )}
      {...props}
    />
  );
}

function FieldLegend({
  className,
  variant = 'legend',
  ...props
}: FieldLegendProps) {
  return (
    <legend
      data-slot='field-legend'
      data-variant={variant}
      className={cn(
        'eye:mb-12 eye:font-medium',
        'eye:data-[variant=legend]:text-16 eye:data-[variant=legend]:leading-22',
        'eye:data-[variant=label]:text-14 eye:data-[variant=label]:leading-22',
        className,
      )}
      {...props}
    />
  );
}

function FieldGroup({ className, ...props }: FieldGroupProps) {
  return (
    <div
      data-slot='field-group'
      className={cn(
        'eye:group/field-group eye:@container/field-group eye:flex eye:w-full eye:flex-col eye:gap-[28px] eye:data-[slot=checkbox-group]:gap-12 eye:*:data-[slot=field-group]:gap-16',
        className,
      )}
      {...props}
    />
  );
}

function Field({ className, orientation = 'vertical', ...props }: FieldProps) {
  return (
    <div
      role='group'
      data-slot='field'
      data-orientation={orientation}
      className={cn(fieldVariants({ orientation }), className)}
      {...props}
    />
  );
}

function FieldContent({ className, ...props }: FieldContentProps) {
  return (
    <div
      data-slot='field-content'
      className={cn(
        'eye:group/field-content eye:flex eye:flex-1 eye:flex-col eye:gap-[6px] eye:leading-22',
        className,
      )}
      {...props}
    />
  );
}

function FieldLabel({
  className,
  children,
  required = false,
  ...props
}: FieldLabelProps) {
  return (
    <Label
      data-slot='field-label'
      className={cn(
        'eye:body-s-highlight',
        'eye:group/field-label eye:peer/field-label eye:flex eye:w-fit eye:gap-4',
        'eye:group-has-disabled/field:text-grey-disabled eye:group-data-[disabled=true]/field:text-grey-disabled',
        'eye:has-[>[data-slot=field]]:w-full eye:has-[>[data-slot=field]]:flex-col eye:has-[>[data-slot=field]]:rounded-md eye:has-[>[data-slot=field]]:border eye:has-[>[data-slot=field]]:border-grey-100 eye:*:data-[slot=field]:p-16',
        className,
      )}
      {...props}
    >
      {children}
      {required && <span aria-hidden='true'>*</span>}
    </Label>
  );
}

function FieldTitle({ className, ...props }: FieldTitleProps) {
  return (
    <div
      data-slot='field-label'
      className={cn(
        'eye:flex eye:w-fit eye:items-center eye:gap-8 eye:text-14 eye:leading-22 eye:font-medium',
        'eye:group-has-disabled/field:text-grey-disabled eye:group-data-[disabled=true]/field:text-grey-disabled',
        className,
      )}
      {...props}
    />
  );
}

function FieldDescription({
  className,
  isError,
  ...props
}: FieldDescriptionProps) {
  if (isError) {
    return <FieldError className={className} {...props} />;
  }

  return (
    <p
      data-slot='field-description'
      className={cn(
        'eye:body-s eye:text-grey-placeholder eye:group-has-data-[orientation=horizontal]/field:text-balance',
        'eye:last:mt-0 eye:nth-last-2:-mt-4 eye:[[data-variant=legend]+&]:-mt-[6px]',
        'eye:[&>a]:underline eye:[&>a]:underline-offset-4 eye:[&>a:hover]:text-blue-100',
        className,
      )}
      {...props}
    />
  );
}

function FieldSeparator({
  children,
  className,
  ...props
}: FieldSeparatorProps) {
  return (
    <div
      data-slot='field-separator'
      data-content={!!children}
      className={cn(
        'eye:relative eye:-my-8 eye:h-20 eye:text-14 eye:group-data-[variant=outline]/field-group:-mb-8',
        className,
      )}
      {...props}
    >
      <Separator className='eye:absolute eye:inset-0 eye:top-1/2' />
      {children && (
        <span
          className='eye:relative eye:mx-auto eye:block eye:w-fit eye:bg-white eye:px-8 eye:text-grey-120'
          data-slot='field-separator-content'
        >
          {children}
        </span>
      )}
    </div>
  );
}

function FieldError({
  className,
  children,
  errors,
  ...props
}: FieldErrorProps) {
  const content = useMemo(() => {
    if (children) {
      return children;
    }

    if (!errors?.length) {
      return null;
    }

    const uniqueErrors = [
      ...new Map(errors.map((error) => [error?.message, error])).values(),
    ];

    if (uniqueErrors?.length == 1) {
      return uniqueErrors[0]?.message;
    }

    return (
      <ul className='eye:ml-16 eye:flex eye:list-disc eye:flex-col eye:gap-4'>
        {uniqueErrors.map((error) => {
          return error?.message && <li key={error.message}>{error.message}</li>;
        })}
      </ul>
    );
  }, [children, errors]);

  if (!content) {
    return null;
  }

  return (
    <div
      role='alert'
      data-slot='field-error'
      className={cn('eye:body-s eye:text-red-100', className)}
      {...props}
    >
      {content}
    </div>
  );
}

export {
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldContent,
  FieldTitle,
};
